import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { Badge } from '@/components/common/Badge'
import { Button } from '@/components/common/Button'
import { PageHeader } from '@/components/common/PageHeader'
import { CommitmentForm } from '@/components/commitments/CommitmentForm'
import { useHostContext } from '@/contexts/HostContext'
import { usePersonaDetail, usePersonaOutlookContact, type OutlookContactStatus } from '@/hooks/usePersonas'
import { synchronizationMessage, useCommitments, type CreateCommitmentInput } from '@/hooks/useCommitments'
import { normalizeTeamsChatEmail, openTeamsChat } from '@/lib/teamsChat'
import './prototype.css'

const roleLabel: Record<string, string> = {
  decisor: 'Decisor',
  influenciador: 'Influenciador',
  operativo: 'Operativo',
  sponsor: 'Sponsor',
  otro: 'Otro',
}

const outlookContactBadge: Record<OutlookContactStatus, { label: string; variant: 'success' | 'warning' | 'info' }> = {
  exists: { label: 'Existe en Outlook', variant: 'success' },
  not_found: { label: 'No encontrado en Outlook', variant: 'warning' },
  sin_email: { label: 'Sin email para comprobar', variant: 'info' },
  unavailable: { label: 'Outlook no disponible', variant: 'warning' },
}

export const PersonaDetalle: React.FC = () => {
  const { personId } = useParams()
  const { host } = useHostContext()
  const { status, persona, error, reload } = usePersonaDetail(personId)
  const outlookContact = usePersonaOutlookContact(personId)
  const { createCommitment, synchronizeCommitment } = useCommitments()
  const [chatStatus, setChatStatus] = React.useState<'idle' | 'opening' | 'error'>('idle')
  const [addingCommitment, setAddingCommitment] = React.useState(false)
  const [commitmentNotice, setCommitmentNotice] = React.useState<string | null>(null)
  const [outlookNotice, setOutlookNotice] = React.useState<string | null>(null)

  const stateMessage = (() => {
    switch (status) {
      case 'loading': return 'Cargando persona…'
      case 'not_signed_in': return 'Inicia sesión con tu cuenta Microsoft para ver esta persona.'
      case 'not_configured': return 'La conexión con SharePoint aún no está habilitada en este entorno.'
      case 'forbidden': return 'Tu perfil aún no está habilitado en Wacner Sales.'
      default: return null
    }
  })()

  if (stateMessage) {
    return (
      <div className="prototypePage">
        <PageHeader title="Persona" subtitle="Contacto en contexto" />
        <p className="prototypeRelationshipRow__summary">{stateMessage}</p>
        <Link to="/personas">← Volver a personas</Link>
      </div>
    )
  }

  if (status === 'unauthorized' || status === 'error' || !persona) {
    return (
      <div className="prototypePage">
        <PageHeader title="Persona" subtitle="Contacto en contexto" />
        <p className="prototypeRelationshipRow__summary">
          No se pudo cargar la persona{error instanceof Error ? `: ${error.message}` : '.'}
        </p>
        <div className="prototypeContextCard__actions">
          <Button size="sm" variant="secondary" onClick={reload}>Reintentar</Button>
          <Link to="/personas"><Button size="sm" variant="ghost">Volver</Button></Link>
        </div>
      </div>
    )
  }

  const chatEmail = normalizeTeamsChatEmail(persona.email)

  const handleOpenChat = async () => {
    if (!chatEmail || chatStatus === 'opening') return
    setChatStatus('opening')
    try {
      await openTeamsChat(chatEmail, host)
      setChatStatus('idle')
    } catch {
      setChatStatus('error')
    }
  }

  const saveCommitment = async (input: CreateCommitmentInput) => {
    const result = await createCommitment(input)
    setAddingCommitment(false)
    setCommitmentNotice(`Compromiso guardado. ${synchronizationMessage(result.synchronization)}`)
    reload()
  }

  const openOutlookContactAction = async () => {
    if (!persona || outlookContact.loading) return
    if (!outlookContact.plan) {
      await outlookContact.refresh()
      return
    }
    const { mode } = outlookContact.plan
    if (mode === 'uncertain') {
      try { await outlookContact.apply('link'); setOutlookNotice('Vínculo verificado.'); reload() } catch { setOutlookNotice('Resultado aún incierto; no se ha repetido la creación.') }
      return
    }
    if (mode !== 'create' && mode !== 'link') { await outlookContact.refresh(); return }
    const verb = mode === 'create' ? 'crear un contacto nuevo en Outlook' : 'vincular el contacto existente de Outlook'
    if (!window.confirm(`¿Confirmas ${verb} para ${persona.email}? No se modificarán contactos existentes.`)) return
    try {
      const result = await outlookContact.apply(mode)
      setOutlookNotice(result.mode === 'created' ? 'Contacto creado en Outlook y vinculado a Sales.' : 'Contacto existente de Outlook vinculado a Sales.')
      reload()
    } catch {
      setOutlookNotice('No se confirmó el vínculo con Outlook. Comprueba el resultado antes de intentar otra operación.')
    }
  }

  return (
    <div className="prototypePage">
      <PageHeader
        title={persona.title}
        subtitle={persona.cargo ?? 'Cargo no registrado'}
        action={<Link to="/personas" className="prototypeMicrosoftLink">Volver a Personas</Link>}
      />

      {chatStatus === 'error' && (
        <p className="prototypeRelationshipRow__summary" role="status">
          Teams no pudo abrir el chat. La persona puede ser externa o no estar disponible en tu organización.
        </p>
      )}

      <section className="prototypeSection prototypePersonOverview" aria-labelledby="persona-contexto-title">
        <div className="prototypeContextHeader">
          <div>
            <div className="prototypeHero__eyebrow">Datos principales</div>
            <h2 id="persona-contexto-title" className="prototypeContextHeader__title">
              {persona.organizacionNombre ?? 'Sin empresa asociada'}
            </h2>
          </div>
          <div className="prototypeContextHeader__chips">
            {persona.rolEnRelacion && <Badge label={roleLabel[persona.rolEnRelacion]} variant="info" size="sm" />}
            <Badge {...outlookContactBadge[persona.outlookContactStatus]} size="sm" />
          </div>
        </div>

        <div className="prototypePersonFacts">
          {persona.organizacionId && (
            <div className="prototypeCompactLine">
              <span>Empresa</span>
              <Link to={`/relaciones/${persona.organizacionId}`} className="prototypeLink">
                {persona.organizacionNombre ?? 'Ver empresa'}
              </Link>
            </div>
          )}
          <div className="prototypeCompactLine">
            <span>Correo</span>
            {persona.email ? (
              <a className="prototypeLink" href={`mailto:${persona.email}`}>{persona.email}</a>
            ) : (
              <span>Sin correo registrado</span>
            )}
          </div>
          <div className="prototypeCompactLine">
            <span>Teléfono</span>
            <span>{persona.telefono ?? 'Sin teléfono registrado'}</span>
          </div>
        </div>

        <div className="prototypePersonActions">
          <div>
            <div className="prototypeMiniLabel">Acciones</div>
            <p className="prototypeRelationshipRow__summary">Continúa el trabajo con esta persona desde Microsoft 365.</p>
          </div>
          <div className="prototypeContextCard__actions">
            <Button
              size="sm"
              variant="secondary"
              disabled={!persona.email || outlookContact.loading}
              onClick={openOutlookContactAction}
              title={!persona.email ? 'La persona no tiene un email para una coincidencia exacta' : undefined}
            >
              {!persona.email
                ? 'Sin email para Outlook'
                : outlookContact.loading ? 'Comprobando Outlook…'
                  : outlookContact.plan?.mode === 'linked' ? 'Contacto Outlook vinculado'
                    : outlookContact.plan?.mode === 'link' ? 'Vincular contacto Outlook'
                      : outlookContact.plan?.mode === 'create' ? 'Crear contacto en Outlook'
                        : outlookContact.plan?.mode === 'ambiguous' ? 'Revisar contacto Outlook'
                          : outlookContact.plan?.mode === 'uncertain' ? 'Comprobar resultado pendiente'
                          : 'Preparar contacto Outlook'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!persona.organizacionId}
              onClick={() => {
                setAddingCommitment((open) => !open)
                setCommitmentNotice(null)
              }}
              title={persona.organizacionId ? 'Registrar una próxima acción' : 'La persona no tiene una empresa asociada'}
            >
              Agregar compromiso
            </Button>
            <Button
              size="sm"
              disabled={!chatEmail || chatStatus === 'opening'}
              onClick={handleOpenChat}
              title={chatEmail ? 'Abrir conversación individual en Microsoft Teams' : 'La persona no tiene un email válido'}
            >
              {chatEmail
                ? chatStatus === 'opening' ? 'Abriendo Teams…' : 'Abrir chat en Teams'
                : 'Sin email para abrir chat'}
            </Button>
            {persona.email && (
              <a className="prototypePersonMailAction" href={`mailto:${persona.email}`}>Escribir correo</a>
            )}
          </div>
        </div>

        {outlookContact.plan?.mode === 'owner_required' && <p role="status">Solo el responsable de la empresa puede vincular su contacto personal Microsoft.</p>}
        {outlookContact.plan?.mode === 'schema_not_ready' && <p role="status">La sincronización aún no está habilitada: falta validar su almacenamiento persistente.</p>}
        {outlookContact.plan?.mode === 'uncertain' && <p role="status">Hay una operación pendiente de verificar. Comprobarla no creará otro contacto.</p>}
        {outlookContact.plan?.mode === 'ambiguous' && (
          <p className="prototypeRelationshipRow__summary" role="status">
            Hay más de un contacto Outlook con ese email exacto. Sales no hará ningún vínculo automáticamente.
          </p>
        )}
        {outlookContact.error && (
          <p className="prototypeRelationshipRow__summary" role="status">
            Outlook no está disponible para preparar este contacto. Intenta nuevamente.
          </p>
        )}
        {outlookNotice && <p className="prototypeCommitmentForm__success" role="status">{outlookNotice}</p>}

        {addingCommitment && persona.organizacionId && (
          <div className="prototypeContextCard prototypePersonCommitmentForm">
            <CommitmentForm
              organizationId={persona.organizacionId}
              organizationName={persona.organizacionNombre ?? 'Empresa asociada'}
              selectedPersonId={persona.id}
              selectedPersonName={persona.title}
              onSave={saveCommitment}
              onCancel={() => setAddingCommitment(false)}
            />
          </div>
        )}
        {commitmentNotice && <p className="prototypeCommitmentForm__success" role="status">{commitmentNotice}</p>}
      </section>

      <section className="prototypeSection">
        <div className="prototypeContextHeader"><div><div className="prototypeHero__eyebrow">Relación</div><h2 className="prototypeContextHeader__title">Historial disponible</h2></div></div>
        {persona.historial.length === 0 ? <p className="prototypeRelationshipRow__summary">Aún no hay interacciones asociadas a esta persona.</p> : <div className="prototypePeopleSearchList">{persona.historial.map((item) => <article key={item.id} className="prototypePeopleSearchRow"><div className="prototypePeopleSearchRow__copy"><strong>{item.title}</strong><span>{item.tipo} · {item.fecha}{item.estado ? ` · ${item.estado}` : ''}</span>{item.detalle && <span>{item.detalle}</span>}</div>{item.tipo === 'compromiso' && <Button size="sm" variant="secondary" onClick={async () => {try {const result=await synchronizeCommitment(item.id.replace('compromiso:',''));setCommitmentNotice(synchronizationMessage(result.synchronization));reload()} catch {setCommitmentNotice('No se confirmó la sincronización. Revisa propietario o conflictos.')}}}>Sincronizar / comprobar To Do</Button>}</article>)}</div>}
      </section>
    </div>
  )
}
